import { useMemo, useState } from 'react';
import { bytesToHex } from '@chainflip/utils/bytes';
import * as ss58 from '@chainflip/utils/ss58';
import classNames from 'classnames';
import { Loader } from '@/shared/components/atoms/Loader';
import Button from '@/shared/components/flip-ui-kit/Button';
import { cacheValidatorCastFields } from '@/shared/graphql/cache/fragments';
import useAuction from '@/shared/hooks/useAuction';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useTracking from '@/shared/hooks/useTracking';
import AddIcon from '@/shared/icons/Add';
import TimePreviousIcon from '@/shared/icons/flip-ui-kit/large/TimePrevious';
import FlipAmount from '@/shared/utils/FlipAmount';
import PreviousRedemptionsModal from './PreviousRedemptionsModal';
import { RegisterNodeModal } from './RegisterNodeModal';
import ValidatorCard from './ValidatorCard';
import ValidatorStatPanel from './ValidatorStatPanel';
import AddNodeSvg from '../../../assets/svg/add-node.svg';
import {
  getLinkedAccounts,
  getValidatorsByIdSs58Query,
} from '../../../graphql/cache/validatorQueries';
import {
  getAccountsRewardsQuery,
  getAccountsSlashedQuery,
  getCurrentEpochQuery,
  getFundedAccountsByFunderQuery,
  getRedemptionsByAccountIdsQuery,
} from '../../../graphql/explorer/validatorQueries';
import { StakeEvents, type StakeTrackEvents } from '../../../types/track';
import { POLL_INTERVAL } from '../../../utils/consts';
import { Layout } from '../Layout';

const AddValidatorCard = ({
  setAddNodeModalActive,
}: {
  setAddNodeModalActive: (active: boolean) => void;
}) => {
  const track = useTracking<StakeTrackEvents>();

  return (
    <div className="bg-holy-radial-6 flex min-h-[406px] w-full flex-col items-center justify-center rounded-md border border-cf-gray-4">
      <AddNodeSvg />
      <div className="mt-[-60px]">
        <Button
          type="secondary-standard"
          onClick={() => {
            track(StakeEvents.AddNode, {
              props: {
                path: window.location.pathname,
                position: 'node_list',
              },
            });
            setAddNodeModalActive(true);
          }}
        >
          Add Node
        </Button>
      </div>
    </div>
  );
};

export default function Nodes({ address }: { address: `0x${string}` }) {
  const track = useTracking<StakeTrackEvents>();

  const { data: fundedAccounts = { accountIds: [], accountIdSs58s: [] } } = useGqlQuery(
    getFundedAccountsByFunderQuery,
    {
      variables: { walletAddress: address },
      refetchInterval: POLL_INTERVAL,
      context: { clientName: 'processor' },
      select: (data) => {
        const accountIds = new Set<number>();
        const accountIdSs58s = new Set<string>();

        data.events?.nodes.forEach((node) => {
          accountIds.add(node.accountId);
          accountIdSs58s.add(node.account.idSs58);
        });

        return { accountIds: [...accountIds], accountIdSs58s: [...accountIdSs58s] };
      },
    },
  );

  const { data: linkedAccounts = [] } = useGqlQuery(getLinkedAccounts, {
    variables: { ethAddress: address, idSs58s: fundedAccounts.accountIdSs58s },
    refetchInterval: POLL_INTERVAL,
    context: { clientName: 'statechainCache' },
    select: (data) => [
      ...new Set([
        ...(data.links?.nodes.map((n) => n.accountIdSs58) ?? []),
        ...(data.unregistered?.nodes.map((n) => n.idSs58) ?? []),
      ]),
    ],
  });

  const { data: validatorsData, isLoading: validatorsLoading } = useGqlQuery(
    getValidatorsByIdSs58Query,
    {
      variables: {
        idSs58s: [...new Set([...fundedAccounts.accountIdSs58s, ...linkedAccounts])],
      },
      enabled: Boolean(fundedAccounts.accountIdSs58s.length || linkedAccounts.length),
      refetchInterval: POLL_INTERVAL,
      context: { clientName: 'statechainCache' },
    },
  );

  // Sort validators by total stake amount
  const validators = useMemo(
    () =>
      FlipAmount.castObject(validatorsData, cacheValidatorCastFields)
        ?.validators?.nodes.map((v) => ({
          ...v,
          unregistered: false,
        }))
        .sort((a, b) => b.totalBalance.cmp(a.totalBalance)) ?? [],
    [validatorsData],
  );

  const unregistered = useMemo(
    () =>
      fundedAccounts.accountIdSs58s
        .filter((acct) => !validatorsData?.validators?.nodes.some((node) => node.idSs58 === acct))
        .map((acct) => ({
          idHex: bytesToHex(ss58.decode(acct).data),
          idSs58: acct,
          isBidding: false,
          isCurrentAuthority: false,
          isCurrentBackup: false,
          isKeyholder: false,
          isOnline: false,
          isQualified: false,
          lockedBalance: FlipAmount.ZERO,
          lockedBalanceValueUsd: '0',
          reputationPoints: 0,
          totalBalance: FlipAmount.ZERO,
          totalBalanceValueUsd: '0',
          unlockedBalance: FlipAmount.ZERO,
          unlockedBalanceValueUsd: '0',
          unregistered: true,
          bid: '0',
          bidValueUsd: '0',
          accountBid: '0',
          accountBidValueUsd: '0',
          totalBid: '0',
          totalBidValueUsd: '0',
        })),
    [fundedAccounts, validatorsData],
  );

  const { auction, isLoading: auctionsLoading, initialized: auctionsInitialized } = useAuction();

  const { data: executedRedemptionsData, isLoading: executedRedemptionsLoading } = useGqlQuery(
    getRedemptionsByAccountIdsQuery,
    {
      variables: {
        accountIds: fundedAccounts.accountIds,
      },
      enabled: Boolean(fundedAccounts.accountIds.length),
      refetchInterval: POLL_INTERVAL,
      context: { clientName: 'processor' },
    },
  );

  const { data: currentEpochData } = useGqlQuery(getCurrentEpochQuery, {
    context: { clientName: 'processor' },
  });

  const { data: validatorRewardsData } = useGqlQuery(getAccountsRewardsQuery, {
    variables: {
      idSs58s: fundedAccounts.accountIdSs58s,
      epochId: currentEpochData?.currentAuthoritySet?.nodes[0].epochId ?? 0,
    },
    enabled: fundedAccounts.accountIdSs58s.length !== 0,
    refetchInterval: validators.length !== 0 ? POLL_INTERVAL : 0,
    context: { clientName: 'processor' },
  });

  const { data: validatorsSlashingData } = useGqlQuery(getAccountsSlashedQuery, {
    variables: {
      idSs58s: fundedAccounts.accountIdSs58s,
      epochId: currentEpochData?.currentAuthoritySet?.nodes[0].epochId ?? 0,
    },
    enabled: fundedAccounts.accountIdSs58s.length !== 0,
    refetchInterval: validators.length !== 0 ? POLL_INTERVAL : 0,
    context: { clientName: 'processor' },
  });

  const totalValidatorsRewardsEpoch = FlipAmount.sum([
    0,
    ...(validatorRewardsData?.accounts?.nodes.map(
      (account) => account.validator?.currentRewards.aggregates?.sum?.reward ?? 0,
    ) ?? []),
  ]);

  const totalValidatorsRewards = FlipAmount.sum([
    0,
    ...(validatorRewardsData?.accounts?.nodes.map(
      (account) => account.validator?.historicRewards.aggregates?.sum?.reward ?? 0,
    ) ?? []),
  ]);

  const totalValidatorSlashed = FlipAmount.sum(
    validatorsSlashingData?.accounts?.nodes.map(
      (acc) => acc.slashedEvents?.aggregates?.sum?.amount ?? 0,
    ) ?? [0],
  );

  const totalValidatorsSlashedEpoch = FlipAmount.sum(
    validatorsSlashingData?.accounts?.nodes.map(
      (acc) => acc.currentEpochslashedEvents?.aggregates?.sum?.amount ?? 0,
    ) ?? [0],
  );

  const [addNodeModalActive, setAddNodeModalActive] = useState(false);
  const [previousRedemptionsModalActive, setPreviousRedemptionsModalActive] = useState(false);

  const isLoading =
    validatorsLoading || (auctionsLoading && !auctionsInitialized) || executedRedemptionsLoading;

  const statsLoading = isLoading;

  const minActiveBid = auction?.minActiveBid ?? FlipAmount.ZERO;

  if (typeof window === 'undefined') {
    return <Layout title="My Nodes" />;
  }

  return (
    <>
      <RegisterNodeModal active={addNodeModalActive} onClose={() => setAddNodeModalActive(false)} />
      <PreviousRedemptionsModal
        active={previousRedemptionsModalActive}
        redemptions={executedRedemptionsData?.events?.nodes}
        heading="Previous Redemptions"
        onCancel={() => setPreviousRedemptionsModalActive(false)}
      />
      <div className="flex flex-1 flex-col">
        <div className="flex w-full flex-col items-start justify-between gap-y-10 py-4 sm:flex-row sm:items-center sm:pb-14">
          <div className="text-35 text-white">My Nodes</div>
          <div className="flex flex-col items-start gap-x-4 gap-y-2 sm:flex-row sm:items-center">
            <Button
              size="small"
              type="link-secondary"
              onClick={() => {
                track(StakeEvents.ViewPreviousRedemptions, {
                  props: {
                    path: window.location.pathname,
                  },
                });
                setPreviousRedemptionsModalActive(true);
              }}
              className="space-x-1 px-0 text-12 sm:text-16"
            >
              <TimePreviousIcon />
              <span>Previous Redemptions</span>
            </Button>
            <Button
              size="medium"
              onClick={() => {
                track(StakeEvents.AddNode, {
                  props: {
                    path: window.location.pathname,
                    position: 'nodes_top_right',
                  },
                });
                setAddNodeModalActive(true);
              }}
            >
              <span className="flex items-center space-x-1">
                <AddIcon className="-ml-2 stroke-current" />
                <span>Add node</span>
              </span>
            </Button>
          </div>
        </div>
        <ValidatorStatPanel
          validators={validators}
          isLoading={statsLoading}
          totalValidatorRewards={totalValidatorsRewards}
          totalValidatorSlashed={totalValidatorSlashed}
          totalValidatorRewardsEpoch={totalValidatorsRewardsEpoch}
          totalValidatorsSlashedEpoch={totalValidatorsSlashedEpoch}
        />
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {validators.concat(unregistered).map((validator) => {
            const val = validatorRewardsData?.accounts?.nodes.find(
              (acc) => validator.idSs58 === acc.idSs58,
            );
            const historicReward = new FlipAmount(
              val?.validator?.historicRewards.aggregates?.sum?.reward ?? '0',
            );

            const currentReward = new FlipAmount(
              val?.validator?.currentRewards.aggregates?.sum?.reward ?? '0',
            );

            return (
              <ValidatorCard
                key={validator.idHex}
                validator={validator}
                minActiveBid={minActiveBid}
                rewards={historicReward ? historicReward.add(currentReward) : FlipAmount.ZERO}
                slashed={
                  new FlipAmount(
                    validatorsSlashingData?.accounts?.nodes?.find(
                      (acc) => acc.idSs58 === validator?.idSs58,
                    )?.slashedEvents?.aggregates?.sum?.amount ?? 0,
                  )
                }
              />
            );
          })}
          <div className={classNames(validators.length === 0 && 'lg:col-span-2')}>
            <AddValidatorCard setAddNodeModalActive={setAddNodeModalActive} />
          </div>
        </div>
        {isLoading && (
          <div className="flex w-full justify-center py-20">
            <Loader message="Fetching data" />
          </div>
        )}
      </div>
    </>
  );
}
